import { metrics } from '@seek/metrics-js';
import { ProfileCardDescription } from '@seek/profile-base-ui';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Card as BraidCard,
  Column,
  Columns,
  ButtonIcon,
  IconEdit,
} from 'braid-design-system';

import { Card } from 'src/components/Card/Card';
import { CardAction } from 'src/components/Card/CardAction/CardAction';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { CREATE_RESUME_PAGE, SUMMARY } from 'src/components/Router';
import { useNavigateRoute } from 'src/components/Router/useNavigateRoute';
import { usePage } from 'src/components/Router/usePage';
import { EDIT } from 'src/consts';
import { SUMMARY_FORM } from 'src/context/FormContext';
import { analyticsClient } from 'src/modules/analytics';

import { setRouteAsTouched } from '../../CreateResume/actionCreators';
import { EditSummary } from '../EditSummary/EditSummary';
import { useSummary } from '../data';

import translations from './.vocab';

const card = SUMMARY;
const mode = EDIT;

export const ReadSummary = () => {
  const { t } = useTranslations(translations);
  const navigate = useNavigateRoute();
  const page = usePage();
  const { summary, loading, error } = useSummary();
  const showLoading = Boolean(loading || error);
  const isEmpty = !showLoading && summary === '';

  const title = isEmpty
    ? t('Add personal summary')
    : t('Edit personal summary');

  const cardAction = isEmpty ? (
    <CardAction
      card={card}
      mode={mode}
      data={{ automation: 'summary-edit' }}
      actionText={t('Add summary')}
      isEmpty={isEmpty}
      onClick={() => {
        analyticsClient.cardOpened({
          currentPageSection: 'personal summary',
          currentPageSectionState: 'add',
        });

        if (page === CREATE_RESUME_PAGE) {
          setRouteAsTouched(SUMMARY);
        }
      }}
    />
  ) : undefined;

  return (
    <>
      <Card
        name="summary"
        cardAction={cardAction}
        title={t('Personal summary')}
        emptyCardText={t(
          'Add a personal summary to your profile as a way to introduce who you are.',
        )}
        isEmpty={isEmpty}
        loading={showLoading}
        loader={
          <BraidCard>
            <LoadingItem type="text" size="standard" width="large" lines={2} />
          </BraidCard>
        }
      >
        <BraidCard>
          <Columns space="small">
            <Column>
              <Text>
                <ProfileCardDescription
                  text={summary}
                  data={{
                    automation: 'summary-read-text',
                    'hj-masked': 'true',
                  }}
                />
              </Text>
            </Column>
            <Column width="content">
              <ButtonIcon
                id="editPersonalSummary"
                icon={<IconEdit />}
                variant="transparent"
                label={t('Edit summary')}
                data={{ automation: 'summary-edit' }}
                onClick={() => {
                  if (isEmpty) {
                    metrics.count('click-empty-card-add-btn', [`card:${card}`]);
                  }

                  analyticsClient.cardOpened({
                    currentPageSection: 'personal summary',
                    currentPageSectionState: 'edit',
                  });

                  if (page === CREATE_RESUME_PAGE) {
                    setRouteAsTouched(SUMMARY);
                  }

                  navigate({
                    card,
                    mode,
                  });
                }}
              />
            </Column>
          </Columns>
        </BraidCard>
      </Card>
      <FormDrawer
        title={title}
        component={EditSummary}
        card={SUMMARY}
        formName={SUMMARY_FORM}
      />
    </>
  );
};
